//src/features/dashboard/components/EmployeeProductivity/HourlyProductivityChart.tsx
import { useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  Label,Cell,
} from 'recharts';

import {
  HourlyProductivityItem,
} from '../../types/Employee/employeeProductivity.types';

interface Props {
  data: HourlyProductivityItem[];
  header: {
    title: string;
  };
}

const COLORS = {
  productive: '#22C55E',
  nonProductive: '#EF4444',
  neutral: '#94A3B8',
  target: '#2563EB',
};

const TargetLabel = ({
  viewBox,
  value,
}: any) => {
  const { x, y, width } = viewBox;

  return (
    <g>
      <rect
        x={x + width - 58}
        y={y - 10}
        rx={4}
        ry={4}
        width={60}
        height={20}
        fill="#FFFFFF"
        stroke="#D0D5DD"
      />

      <text
        x={x + width - 30}
        y={y + 3}
        textAnchor="middle"
        fontSize="10"
        fill="#2563EB"
      >
        {value}% target
      </text>
    </g>
  );
};

const CustomLegend = ({
  hoveredKey,
  setHovered,
}: {
  hoveredKey: "productive" | "nonProductive" | "neutral" | null;
  setHovered: React.Dispatch<
    React.SetStateAction<{
      index: number | null;
      key: "productive" | "nonProductive" | "neutral" | null;
    }>
  >;
}) => (
  <div className="mb-6 flex justify-center gap-6">
    {[
      {
        key: "productive",
        label: "Productive",
        color: "#22C55E",
      },
      {
        key: "nonProductive",
        label: "Non-Prod",
        color: "#EF4444",
      },
      {
        key: "neutral",
        label: "Neutral",
        color: "#94A3B8",
      },
    ].map((item) => (
      <div
        key={item.key}
        className="flex cursor-pointer items-center gap-2"
        onMouseEnter={() =>
          setHovered({
            index: null,
            key: item.key as
              | "productive"
              | "nonProductive"
              | "neutral",
          })
        }
        onMouseLeave={() =>
          setHovered({
            index: null,
            key: null,
          })
        }
      >
        <span
          className="h-3 w-3 rounded-sm transition-opacity"
          style={{
            background: item.color,
            opacity:
              hoveredKey === null || hoveredKey === item.key
                ? 1
                : 0.3,
          }}
        />

        <span
          className="text-[13px]"
          style={{
            opacity:
              hoveredKey === null || hoveredKey === item.key
                ? 1
                : 0.3,
            fontWeight:
              hoveredKey === item.key ? 600 : 400,
          }}
        >
          {item.label}
        </span>
      </div>
    ))}
  </div>
);

const CustomTooltip = ({
  active,
  payload,
  label,
  hovered,
}: any) => {
  if (!active || !payload?.length) return null;

  // Find the hovered segment
const current = payload.find(
  (item: any) => item.dataKey === hovered.key
);

if (!current) return null;


  const colorMap: Record<string, string> = {
    productive: "#22C55E",
    nonProductive: "#EF4444",
    neutral: "#94A3B8",
  };

  const labelMap: Record<string, string> = {
    productive: "Productive",
    nonProductive: "Non-Prod",
    neutral: "Neutral",
  };

  return (
    <div className="rounded-lg border border-[#D0D5DD] bg-white px-3 py-2 shadow-lg">
      <div className="mb-2 text-[12px] font-medium text-[#101828]">
        {label}
      </div>

      <div className="flex items-center gap-2">
        <span
          className="h-3 w-3 rounded-full"
          style={{
            backgroundColor:
             colorMap[current.dataKey] ,
          }}
        />

        <span className="text-[12px] text-[#344054]">
          {labelMap[current.dataKey]}:
        </span>

        <span className="text-[12px] font-semibold text-[#101828]">
          {current.value}%
        </span>
      </div>
    </div>
  );
};
const HourlyProductivityChart = ({
  data,
  header,
}: Props) => {

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  
const [hovered, setHovered] = useState<{
  index: number | null;
  key: "productive" | "nonProductive" | "neutral" | null;
}>({
  index: null,
  key: null,
});


const productiveAvg =
  data.length > 0
    ? Math.round(
        data.reduce((sum, x) => sum + x.productive, 0) / data.length
      )
    : 0;

const nonProductiveAvg =
  data.length > 0
    ? Math.round(
        data.reduce((sum, x) => sum + x.nonProductive, 0) / data.length
      )
    : 0;

const neutralAvg =
  data.length > 0
    ? Math.round(
        data.reduce((sum, x) => sum + x.neutral, 0) / data.length
      )
    : 0;

const target = data.length > 0 ? data[0].target : 80;

console.log("Hourly Data:", data);
console.log("Target Hourly:", target);
    
  return (
    <div
className="
rounded-[18px]
border
border-[#D0D5DD]
bg-white
px-5
pt-4
pb-5
"
>{/* Header */}

      <div
  className="
    mb-4
    flex
    items-center
    justify-between
  "
>
        <div className="mb-4 flex items-center gap-4">
  <h3 className="text-[15px] font-semibold text-[#002B5B] whitespace-nowrap">
    Hourly Productivity · Today
  </h3>

  <div
  className="
    rounded-full
    bg-[#EEF4FF]
    px-3
    py-[4px]
    text-[11px]
    font-medium
    text-[#2563EB]
  "
>
  Productive {productiveAvg}% · Non-Prod {nonProductiveAvg}% · Neutral {neutralAvg}% time per hour
</div>
</div>
      </div>

      <CustomLegend
  hoveredKey={hovered.key}
  setHovered={setHovered}
/>

      <div className="h-[215px]">
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <BarChart
  data={data}
  margin={{
    top: 20,
    right: 35,
    left: 10,
    bottom: 0,
  }}
onMouseMove={(state) => {
  if (typeof state?.activeTooltipIndex === "number") {
    setActiveIndex(state.activeTooltipIndex);
  }
}}

onMouseLeave={() => setActiveIndex(null)}

>
          <CartesianGrid
    stroke="#EAECF0"
    strokeDasharray="3 3"
    vertical={false}
/>

                 <XAxis
    interval={0}
    tickMargin={12}
    axisLine={false}
    tickLine={false}
    tick={{
        fontSize:10,
        fill:"#475467",
    }}
/>

        <YAxis
  domain={[0, 100]}
  width={34}
  axisLine={false}
  tickLine={false}
  tick={{
    fontSize: 10,
    fill: "#344054",
  }}
  tickFormatter={(value: number) => `${value}%`}
/>

           <Tooltip
  content={
    <CustomTooltip hovered={hovered} />
  }
/>

            {/* <Legend content={<CustomLegend />} /> */}

 <ReferenceLine
  y={target}
  stroke="#adbfc7ff"
  strokeWidth={2}
  strokeDasharray="6 4"
  ifOverflow="extendDomain"
  label={({ viewBox }) => {
    if (!viewBox) return null;

    const { x, y, width } = viewBox;

    return (
      <g>
        <rect
          x={x + width - 72}
          y={y - 22}
          width={68}
          height={20}
          rx={4}
          fill="#ffffff9d"
          stroke="#2563EB"
        />
        <text
          x={x + width - 38}
          y={y - 6}
          textAnchor="middle"
          fontSize={10}
          fill="#2563EB"
        >
          {target}% target
        </text>
      </g>
    );
  }}
/>
  
{/* </ReferenceLine> */}

    <Bar
  dataKey="productive"
  stackId="time"
  barSize={50}
  onMouseOver={(_, index) =>
    setHovered({
      index,
      key: "productive",
    })
  }
  onMouseOut={() =>
    setHovered({
      index: null,
      key: null,
    })
  }
>
  {data.map((_, index) => (
    <Cell
      key={index}
    fill="#22C55E"
fillOpacity={
  hovered.key === null
    ? 1
    : hovered.key === "productive"
    ? 1
    : 0.25
}
    />
  ))}
</Bar>


<Bar
  dataKey="nonProductive"
  stackId="time"
  barSize={50}
  onMouseOver={(_, index) =>
    setHovered({
      index,
      key: "nonProductive",
    })
  }
  onMouseOut={() =>
    setHovered({
      index: null,
      key: null,
    })
  }
>
  {data.map((_, index) => (
    <Cell
      key={index}
     fill="#EF4444"
fillOpacity={
  hovered.key === null
    ? 1
    : hovered.key === "nonProductive"
    ? 1
    : 0.25
}
    />
  ))}
</Bar>

<Bar
  dataKey="neutral"
  stackId="time"
  barSize={50}
  onMouseOver={(_, index) =>
    setHovered({
      index,
      key: "neutral",
    })
  }
  onMouseOut={() =>
    setHovered({
      index: null,
      key: null,
    })
  }
>
  {data.map((_, index) => (
    <Cell
      key={index}
     fill="#94A3B8"
fillOpacity={
  hovered.key === null
    ? 1
    : hovered.key === "neutral"
    ? 1
    : 0.25
}
    />
  ))}
</Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

   

      </div>
  );
};

export default HourlyProductivityChart;